import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createResource,
  deleteResource,
  resetResourceState,
} from "../../../../redux/thunks/Resource";
import VideoRecorder from "../../../../components/candidate-assessment-flow/VideoRecorder";
import { PersonalityTestQuestionTypes } from "../../../../utils/Constants";
import ProcessingIndicator from "../../../../components/common/ProcessingIndicator";
import TextArea from "../../../../components/sub-component/TextArea";
import Button from "../../../../components/sub-component/Button";
import { useFocusWithin } from "@react-aria/interactions";
import Timer from "../../../../components/common/Timer";
import TestContentWrapper from "./TestContentWrapper";
import SpeechProgress from "../../../../components/candidate-assessment-flow/SpeechProgress";
import useSpeechWrapper from "../../../../utils/contexts/SpeechWrapper";
import { isSupportedSpeechBrowser } from "../../../../utils/utilities";

export default function PersonalityTestCS(props) {
  const {
    question,
    currentQuestionNo,
    totalQuestions,
    assessmentId,
    processing,
  } = props;

  const isFirefox =
    typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);
  const dispatch = useDispatch();
  const resource = useSelector((state) => state.resourceReducer);

  const [answer, setAnswer] = useState("");
  const [isUserTryingToRetry, setIsUserTryingToRetry] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [videoResourceId, setVideoResourceId] = useState(null);
  const [uploadBlobURL, setUploadBlobURL] = useState(null);
  const [error, setError] = useState();
  const [pVRDuration, setPVRDuration] = useState(0);
  const validAnswerLength = answer?.length >= 250;
  // mode can be 'vad' or 'stt' and target is in seconds(vad) or characters(stt)
  const speech = useSpeechWrapper({ mode: "vad", target: 25 });

  const startListening = () => {
    speech?.reset();
    speech?.start();
  };

  const stopListening = () => {
    speech?.stop();
  };

  const resetTranscriptData = () => {
    speech?.reset();
  };

  const handleVideoFileUpload = async (blobURL) => {
    console.log("VIdeo Uploading start...");
    setError("");
    const blob = await fetch(blobURL).then((r) => r.blob());
    const file = new File([blob], `${Date.now().toString()}.mp4`, {
      type: "video/mp4",
    });
    setIsVideoUploading(true);
    console.log("VIdeo Uploading start...");
    const formData = new FormData();
    formData.append("file", file);
    console.log("VIdeo Uploading start...");
    const { payload, meta } = await dispatch(
      createResource({ data: formData, signal: null }),
    );
    console.log("VIdeo Uploading endpoint gets trigger");

    if (meta.requestStatus === "rejected") {
      setError(
        "Failed to upload video. Please check your internet connection and retry.",
      );
      setIsVideoUploading(false);
      return;
    } else {
      setVideoResourceId(payload.id);
      localStorage.setItem("video_recorder_id", payload.id);
      setUploadBlobURL(null);
      setIsVideoUploading(false);
      console.log("VIdeo Uploading done...");
    }
  };

  const onQuestionSubmit = (retry = false) => {
    let data = null;

    if (question.type === PersonalityTestQuestionTypes.VIDEO_RESPONSE) {
      data = {
        assess_id: assessmentId,
        ...(retry ? { retry: true } : { resource_id: resource.data?.id }),
      };
    } else {
      data = {
        assess_id: assessmentId,
        ...(answer ? { response_text: answer } : null),
      };
    }
    if (props.onQuestionSubmit && data) {
      props.onQuestionSubmit(data);
    }
  };

  const remainTime = useMemo(() => {
    if (!question) return 0;

    if (error) {
      setError(null);
    }
    const serveTime = new Date(question.serve_time);
    const duration = question.duration * 1000;

    const remainingTime = (serveTime.getTime() + duration - Date.now()) / 1000;
    return Number(remainingTime.toFixed(0));
  }, [question]);

  useEffect(() => {
    setAnswer(null);
    setVideoResourceId(null);
    setUploadBlobURL(null);
    setPVRDuration(0);
  }, [question]);

  useEffect(() => {
    if (!question) return;
    speech?.stop();
    speech?.reset();
  }, [question, currentQuestionNo]);

  useEffect(() => {
    dispatch(resetResourceState());
  }, [question]);

  const deleteVideo = async (videoId) => {
    await dispatch(deleteResource({ id: videoId }));
    setVideoResourceId(null);
  };

  useEffect(() => {
    if (isUserTryingToRetry) {
      // deleteVideo(videoResourceId);
      setIsUserTryingToRetry(false);
    }
  }, [isUserTryingToRetry]);

  const [timeDuration, setTimeDuration] = useState(0);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: (e) => {},
    onBlurWithin: (e) => {
      setIsSkipModalOpen(false);
    },
  });

  useMemo(() => {
    if (timeDuration === 0) {
      setIsSkipModalOpen(false);
    }
  }, [timeDuration]);

  const [recorderStatus, setRecorderStatus] = useState("");

  const handleStatusChange = (newStatus) => {
    setRecorderStatus(newStatus);
  };

  useEffect(() => {
    if (recorderStatus) {
      // Status change handled
    }
  }, [recorderStatus]);

  useEffect(() => {
    let interval;

    if (recorderStatus === "recording") {
      interval = setInterval(() => {
        setPVRDuration((prev) => {
          return prev + 1;
        });
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [recorderStatus]);

  useEffect(() => {
    if (recorderStatus === "idle") {
      setPVRDuration(0);
    }
  }, [recorderStatus]);

  let hasEnoughSpeech = true;

  if (speech?.mode === "vad") {
    hasEnoughSpeech = speech?.progress === 100;
  } else if (speech?.mode === "stt") {
    if (isSupportedSpeechBrowser()) {
      hasEnoughSpeech = speech?.progress === 100;
    } else {
      hasEnoughSpeech = true;
    }
  }

  const needsDuration = speech?.mode === "stt" && !isSupportedSpeechBrowser();
  const hasOneMinute = needsDuration ? pVRDuration >= 60 : true;

  const isRecordingStopped = recorderStatus === "stopped";
  const hasVideoId = !!videoResourceId;
  const isUploadComplete = resource?.upload_percentage === 100 || hasVideoId;

  const canSubmitVideo = localStorage.getItem("video_recorder_id")
    ? true
    : hasOneMinute && hasEnoughSpeech && isRecordingStopped && isUploadComplete;

  return (
    <TestContentWrapper
      title={null}
      duration={remainTime}
      currentQuestion={currentQuestionNo}
      totalQuestions={totalQuestions || 0}
      processing={processing}
    >
      {(() => {
        if (!question) {
          return (
            <div className="align-self-center">
              <ProcessingIndicator className={"medium-indicator"} />
            </div>
          );
        }

        return (
          <div className="assessment-test-container">
            <div
              className="d-flex flex-column w-100 justify-content-center align-items-start"
              style={{ gap: "12px" }}
            >
              <div className="w-100 d-flex flex-row align-items-start justify-content-between">
                <span className="subtitle-2 dark-100 text-start">{`Question ${currentQuestionNo} :`}</span>
              </div>
              <span
                className="body-1 dark-100 disable-text-selection text-start"
                dangerouslySetInnerHTML={{ __html: question.text }}
              ></span>
              {question.type === PersonalityTestQuestionTypes.TEXT_RESPONSE && (
                <div
                  className="w-100 d-flex flex-column align-items-start"
                  style={{
                    maxWidth: "100%",
                    gap: "12px",
                  }}
                >
                  <TextArea
                    inputStyle={{ minHeight: "240px", padding: "25px" }}
                    value={answer}
                    onPaste={(e) => {
                      e.preventDefault();
                    }}
                    onChange={(e) => {
                      setAnswer(e.target.value);
                    }}
                    placeholder="Write your answer here..."
                  />
                  {!validAnswerLength && (
                    <span className="body-2 dark-50 text-start">
                      {answer?.length > 0
                        ? `${250 - answer?.length} more to go…`
                        : "Enter atleast 250 characters"}
                    </span>
                  )}
                </div>
              )}
              {question.type ===
                PersonalityTestQuestionTypes.VIDEO_RESPONSE && (
                <div
                  className="w-100 d-flex flex-column align-items-center"
                  style={{ maxWidth: "100%", gap: "12px" }}
                >
                  <div
                    className="w-100 d-flex flex-column align-items-center"
                    style={{ maxWidth: "770px", gap: "12px" }}
                  >
                    {speech?.loading ? (
                      <div
                        className="w-100 d-flex justify-content-center align-items-center"
                        style={{ minHeight: "400px" }}
                      >
                        <ProcessingIndicator className={"medium-indicator"} />
                      </div>
                    ) : (
                      <>
                        <VideoRecorder
                          startListening={startListening}
                          stopListening={stopListening}
                          resetTranscriptData={resetTranscriptData}
                          onStatusChange={handleStatusChange}
                          // status={status}
                          // startRecording={startRecording}
                          // stopRecording={stopRecording}
                          // mediaBlobUrl={mediaBlobUrl}
                          // clearBlobUrl={clearBlobUrl}
                          // previewStream={previewStream}
                          recordingDuration={question?.duration}
                          height="100%"
                          width="100%"
                          onVideoSubmit={(blobURL) => {
                            setUploadBlobURL(blobURL);
                            handleVideoFileUpload(blobURL);
                          }}
                          processingUpload={resource.processing}
                          uploadProgress={resource.upload_percentage || 0}
                          camMustActive={true}
                          onRetry={() => {
                            setIsUserTryingToRetry(true);
                          }}
                          isSomthingIsInProcess={resource.processing}
                          setPVRDuration={setPVRDuration}
                          isPersonalityTest={true}
                          progress={speech?.progress || 0}
                          message={speech?.message || ""}
                        />
                        {error && (
                          <span className="body-2 danger-text text-start">
                            {error}
                            <span
                              className="dark-100 pointer"
                              onClick={() => {
                                setIsSkipModalOpen(false);
                                setError("");
                                handleVideoFileUpload(uploadBlobURL);
                              }}
                              style={{
                                textDecoration: "underline",
                                marginLeft: "2px",
                              }}
                            >
                              Upload Again
                            </span>
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              <div
                className="w-100 d-flex justify-content-end align-items-end"
                style={{ marginTop: "12px" }}
              >
                <div tabIndex={-1} {...focusWithinProps}>
                  {isSkipModalOpen ? (
                    <Button
                      processing={processing}
                      btn="ragular-btn"
                      varrient={"primary"}
                      title={"Skip Question?"}
                      style={{ width: "max-content", height: "40px" }}
                      onClick={() => {
                        const data = {
                          assess_id: assessmentId,
                        };
                        if (props.onQuestionSubmit) {
                          props.onQuestionSubmit(data);
                        }
                        setError("");
                        setIsSkipModalOpen(false);
                      }}
                    />
                  ) : (
                    <>
                      {question?.type ===
                        PersonalityTestQuestionTypes.VIDEO_RESPONSE && (
                        <Button
                          processing={processing}
                          disabled={processing || !canSubmitVideo}
                          btn="ragular-btn"
                          varrient="primary"
                          title={
                            resource.processing
                              ? "Uploading..."
                              : currentQuestionNo === totalQuestions || 0
                                ? "Submit"
                                : "Next"
                          }
                          style={{ width: "120px", height: "40px" }}
                          onClick={() => {
                            const tempAnswer =
                              question?.type ===
                              PersonalityTestQuestionTypes.VIDEO_RESPONSE
                                ? videoResourceId
                                  ? videoResourceId
                                  : true
                                : true;

                            if (!tempAnswer) {
                              setIsSkipModalOpen(true);
                            } else if (!isSkipModalOpen) {
                              onQuestionSubmit();
                              setIsSkipModalOpen(false);
                              if (localStorage.getItem("video_recorder_id")) {
                                localStorage.removeItem("video_recorder_id");
                              }
                            }
                          }}
                        />
                      )}

                      {question?.type ===
                        PersonalityTestQuestionTypes.TEXT_RESPONSE && (
                        <Button
                          processing={processing}
                          disabled={resource.processing || !validAnswerLength}
                          btn="ragular-btn"
                          varrient="primary"
                          title={
                            resource.processing
                              ? "Uploading..."
                              : currentQuestionNo === totalQuestions || 0
                                ? "Submit"
                                : "Next"
                          }
                          style={{ width: "120px", height: "40px" }}
                          onClick={() => {
                            const tempAnswer =
                              question?.type ===
                              PersonalityTestQuestionTypes.TEXT_RESPONSE
                                ? answer
                                  ? answer !== ""
                                  : ""
                                : true;

                            if (!tempAnswer) {
                              setIsSkipModalOpen(true);
                            } else if (!isSkipModalOpen) {
                              onQuestionSubmit();
                              setIsSkipModalOpen(false);
                            }
                          }}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </TestContentWrapper>
  );
}
